/*
 * The Scopie logo: the lowercase "scopie" wordmark with the yellow dot in the "o" (B1 concept),
 * drawn as inline SVG so it needs no request and scales with `height`. The same artwork is in
 * public/brand/scopie-wordmark*.svg for places that need an image URL (<img>, emails, OG cards).
 */
import { BRAND_NAME } from '@/config/brand';

const NAVY = '#0f2237';
const YELLOW = '#ffc400';

interface ScopieLogoProps {
  /** 'light' for light backgrounds (navy wordmark), 'dark' for navy backgrounds (white wordmark). */
  variant?: 'light' | 'dark';
  /** Height of the logo in px; the width follows the artwork's aspect ratio. */
  size?: number;
  /** Show only the square app icon (ring and dot) instead of the wordmark. */
  markOnly?: boolean;
  className?: string;
}

export function ScopieLogoMark({ size = 30 }: Pick<ScopieLogoProps, 'size'>) {
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" aria-hidden="true" focusable="false">
      <rect width="512" height="512" rx="112" fill={NAVY} />
      <circle cx="256" cy="256" r="122" fill="none" stroke="#fff" strokeWidth="84" />
      <circle cx="256" cy="256" r="52" fill={YELLOW} />
    </svg>
  );
}

export function ScopieWordmark({ variant = 'light', size = 30 }: Pick<ScopieLogoProps, 'variant' | 'size'>) {
  const ink = variant === 'dark' ? '#fff' : NAVY;
  return (
    <svg width={Math.round((size * 554) / 196)} height={size} viewBox="-2 -6 554 196" aria-hidden="true" focusable="false">
      <path d="M70.7 62.1 A28 19 0 1 0 46 90 A28 19 0 1 1 21.8 118.5" fill="none" stroke={ink} strokeWidth="24"/>
      <path d="M177.5 65.2 A37 37 0 1 0 177.5 114.8" fill="none" stroke={ink} strokeWidth="26"/>
      <circle cx="250" cy="90" r="37" fill="none" stroke={ink} strokeWidth="26"/>
      <circle cx="250" cy="90" r="12" fill={YELLOW}/>
      <path d="M308 40 H334 V168 a8 8 0 0 1 -8 8 H316 a8 8 0 0 1 -8 -8 Z" fill={ink}/>
      <circle cx="358" cy="90" r="37" fill="none" stroke={ink} strokeWidth="26"/>
      <rect x="416" y="40" width="26" height="100" fill={ink}/>
      <circle cx="430" cy="14" r="17" fill={ink}/>
      <path d="M537 90 A37 37 0 1 0 523.8 118.3" fill="none" stroke={ink} strokeWidth="26"/>
      <rect x="463" y="80" width="87.0" height="20" fill={ink}/>
    </svg>
  );
}

export function ScopieLogo({ variant = 'light', size = 30, markOnly = false, className }: ScopieLogoProps) {
  return (
    <span className={className} style={{ display: 'inline-flex', alignItems: 'center' }}>
      {markOnly ? <ScopieLogoMark size={size} /> : <ScopieWordmark variant={variant} size={size} />}
      <span className="sr-only">{BRAND_NAME}</span>
    </span>
  );
}
